import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import ProgrammeService from '../services/ProgrammeService';
import ActivityService from '../services/ActivityService';
import AssignmentService from '../services/AssignmentService';
import ExternalMessageService from '../services/ExternalMessageService';
import AttendanceService from '../services/AttendanceService';
import { useStudentPortal } from '../store/StudentPortalContext';
import { formatDate } from '../utils/dateFormat';

export default function StudentPortalTimetableDetailPage() {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const { student } = useStudentPortal();
  const programmeService = useMemo(() => new ProgrammeService(), []);
  const activityService = useMemo(() => new ActivityService(), []);
  const assignmentService = useMemo(() => new AssignmentService(), []);
  const messageService = useMemo(() => new ExternalMessageService(), []);
  const attendanceService = useMemo(() => new AttendanceService(), []);

  const programmes = useMemo(() => programmeService.list(), [programmeService]);
  const activities = useMemo(() => activityService.list(), [activityService]);

  const detailItem = useMemo(() => {
    if (type === 'activity') {
      return activities.find((activity) => activity.id === id) || null;
    }
    return programmes.find((programme) => programme.id === id) || null;
  }, [activities, programmes, type, id]);

  const assignments = useMemo(
    () => (student ? assignmentService.listForStudents([student.id]) : []),
    [assignmentService, student]
  );

  const messageCount = useMemo(
    () => (student ? messageService.listForStudent(student.id).length : 0),
    [messageService, student]
  );

  const attendanceRate = student?.attendance?.rate || 0;
  const threshold = attendanceService.resolveThreshold();
  const attendanceStatus = attendanceRate < threshold ? 'Below threshold' : 'On track';
  const attendanceVariant = attendanceRate < threshold ? 'danger' : 'success';
  const absenceReasons = student?.attendance?.absenceReasons || [];
  const attendanceLogs = student?.attendance?.logs || [];
  const isProgramme = type !== 'activity';
  const scheduleLabel = isProgramme
    ? `${detailItem.day || detailItem.days?.join(', ') || 'TBD'} · ${detailItem.time || 'TBD'}`
    : `${detailItem.date ? formatDate(detailItem.date) : 'TBD'} · ${detailItem.time || 'TBD'}`;
  const leadName = detailItem.lead || detailItem.staff || 'TBD';
  const capacityLabel = `${detailItem.enrolled || 0}/${detailItem.capacity || 0}`;

  if (!detailItem) {
    return (
      <div className="space-y-4">
        <Button size="sm" variant="secondary" onClick={() => navigate('/student-portal/timetable')}>
          Back to timetable
        </Button>
        <Card className="p-6">
          <p className="sp-muted">This timetable item could not be found.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="space-y-2">
          <Button size="sm" variant="ghost" onClick={() => navigate('/student-portal/timetable')} type="button">
            ← Back to timetable
          </Button>
          <div>
            <h2 className="sp-value" style={{ fontSize: '22px', fontWeight: 600 }}>
              {detailItem.name || 'Course details'}
            </h2>
            <p className="text-sm sp-muted">{scheduleLabel}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="active">{isProgramme ? 'Programme' : 'Activity'}</Badge>
            {detailItem.status && <Badge variant="review">{detailItem.status}</Badge>}
            <Badge variant="inactive">Lead: {leadName}</Badge>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Card className="p-4 space-y-1">
          <div className="text-xs sp-muted">Schedule</div>
          <div className="sp-value" style={{ fontWeight: 600 }}>{scheduleLabel}</div>
          <div className="text-xs sp-muted">{isProgramme ? 'Weekly session' : 'One-off activity'}</div>
        </Card>
        <Card className="p-4 space-y-1">
          <div className="text-xs sp-muted">Location</div>
          <div className="sp-value" style={{ fontWeight: 600 }}>{detailItem.location || 'Centre'}</div>
          <div className="text-xs sp-muted">{detailItem.meetingPoint || detailItem.collectionPlan || 'Check with staff'}</div>
        </Card>
        <Card className="p-4 space-y-1">
          <div className="text-xs sp-muted">Capacity</div>
          <div className="sp-value" style={{ fontWeight: 600 }}>{capacityLabel}</div>
          <div className="text-xs sp-muted">Students enrolled</div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5 space-y-3">
          <div className="sp-label">Overview</div>
          <div className="text-sm sp-muted space-y-2">
            <div>Lead: {leadName}</div>
            <div>Capacity: {capacityLabel}</div>
            {detailItem.staffAvailability && <div>Staff required: {detailItem.staffAvailability}</div>}
            {detailItem.supervisionRatio && <div>Supervision ratio: {detailItem.supervisionRatio}</div>}
          </div>
          {detailItem.notes && <div className="text-sm sp-muted mt-2">{detailItem.notes}</div>}
          {detailItem.safeguarding && (
            <div className="sp-panel sp-panel--muted p-3 text-xs space-y-1">
              <div className="sp-label">Safety & safeguarding</div>
              {detailItem.safeguarding.consentRequired && (
                <div>Consent required: {detailItem.safeguarding.consentStatus || 'Pending'}</div>
              )}
              {detailItem.safeguarding.riskAssessmentComplete !== undefined && (
                <div>Risk assessment: {detailItem.safeguarding.riskAssessmentComplete ? 'Complete' : 'Pending'}</div>
              )}
              {detailItem.safeguarding.meetingPoint && <div>Meeting point: {detailItem.safeguarding.meetingPoint}</div>}
              {detailItem.safeguarding.collectionPlan && <div>Collection plan: {detailItem.safeguarding.collectionPlan}</div>}
            </div>
          )}
        </Card>

        <Card className="p-5 space-y-3">
          <div className="sp-label">Your attendance</div>
          <div className="flex items-center gap-2">
            <Badge variant={attendanceVariant}>{attendanceRate}%</Badge>
            <span className="text-sm sp-muted">{attendanceStatus}</span>
          </div>
          {student?.attendance?.last8Weeks?.length > 0 && (
            <div className="text-xs sp-muted">
              Last 8 weeks: {student.attendance.last8Weeks.join('% · ')}%
            </div>
          )}
          {absenceReasons.length > 0 && (
            <div className="text-xs sp-muted space-y-1">
              <div className="sp-label">Absence reasons</div>
              {absenceReasons.map((reason, idx) => (
                <div key={`${reason.date}-${idx}`}>{formatDate(reason.date)} · {reason.reason || 'Recorded'}</div>
              ))}
            </div>
          )}
          {attendanceLogs.length > 0 && (
            <div className="text-xs sp-muted space-y-1 sp-scroll-section">
              <div className="sp-label">Session log</div>
              {attendanceLogs.slice(0, 6).map((log, idx) => (
                <div key={`${log.date}-${idx}`}>{formatDate(log.date)} · {log.status}</div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5 space-y-3">
          <div className="sp-label">Lesson plans & documents</div>
          {detailItem.lessonPlans?.length === 0 && detailItem.documents?.length === 0 ? (
            <div className="text-sm sp-muted">No lesson plans or documents yet.</div>
          ) : (
            <div className="space-y-3 sp-scroll-section">
              {(detailItem.lessonPlans || []).map((plan) => (
                <div key={plan.id || plan.title} className="sp-panel sp-panel--muted p-3 text-sm space-y-1">
                  <div className="sp-value" style={{ fontWeight: 600 }}>
                    {plan.title || plan.fileName || 'Lesson plan'}
                  </div>
                  <div className="text-xs sp-muted">
                    {plan.date ? formatDate(plan.date) : 'No date set'}
                    {plan.archived ? ' · Archived' : ''}
                    {plan.linkUrl && (
                      <>
                        {' · '}
                        <a className="sp-link" href={plan.linkUrl} target="_blank" rel="noreferrer">
                          Open link
                        </a>
                      </>
                    )}
                  </div>
                </div>
              ))}
              {(detailItem.documents || []).map((doc) => (
                <div key={doc.id || doc.name} className="sp-panel sp-panel--muted p-3 text-sm space-y-1">
                  <div className="sp-value" style={{ fontWeight: 600 }}>
                    {doc.name || doc.fileName || 'Document'}
                  </div>
                  <div className="text-xs sp-muted">
                    {doc.archived ? 'Archived' : 'Active'}
                    {doc.linkUrl && (
                      <>
                        {' · '}
                        <a className="sp-link" href={doc.linkUrl} target="_blank" rel="noreferrer">
                          Open link
                        </a>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-5 space-y-3">
          <div className="sp-label">Your activity</div>
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <Badge variant="inactive">Assignments: {assignments.length}</Badge>
            <Badge variant="inactive">Messages with staff: {messageCount}</Badge>
          </div>
          {assignments.length > 0 ? (
            <div className="mt-2 space-y-2 sp-scroll-section">
              {assignments.slice(0, 5).map((assignment) => (
                <div key={assignment.id} className="sp-panel sp-panel--muted p-3">
                  <div className="sp-value" style={{ fontWeight: 600 }}>{assignment.title}</div>
                  <div className="text-xs sp-muted">
                    Due {assignment.dueDate ? formatDate(assignment.dueDate) : 'No due date'}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm sp-muted">No assignments recorded yet.</div>
          )}
        </Card>
      </div>
    </div>
  );
}
